import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Paper } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useTableData } from "../hooks/useTableData";
import { type GasAllocationTarget } from "../models/GasAllocationTarget";
import { getGasAllocationTargets } from "../repositories/GasAllocationsRepository";
import { EditGasDefaultsModal } from "./EditGasDefaultsModal";

export const useGasAllocationTargets = () =>
	useQuery<GasAllocationTarget[], Error>({
		queryKey: ["gas-allocation-targets"],
		queryFn: ({ signal }) => getGasAllocationTargets(signal),
	});

export const TableGasAllocations = () => {
	const [showModal, setShowModal] = useState<"update" | "">("");
	const [error, setError] = useState("");

	const { data, isLoading, error: queryError } = useGasAllocationTargets();

	const targets = data ?? [];
	const total = targets.reduce(
		(total, target) => total + (target.DefaultAllocationPercent ?? 0),
		0,
	);

	const { columnDefs } = useTableData();
	const { defaultColDef } = useGridDefinitions({});

	useEffect(() => {
		if (queryError) setError(queryError.message);
	}, [queryError]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Gas Allocations
						</Typography>
						<Typography variant="body2" color="textSecondary">
							The share each target starts a new day from on the Gas Reading
							screen, out of what the plant's three meters read.
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
							<AgGridReact<GasAllocationTarget>
								loading={isLoading}
								rowData={targets}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.TargetId}`}
							/>
						</Paper>
					</Grid2>
					<Grid2
						container
						size={{ md: 12, xs: 12 }}
						alignItems="center"
						justifyContent="flex-end"
						spacing={2}
						sx={{ pt: 1 }}
					>
						{!isLoading && targets.length > 0 && (
							<Typography
								variant="body2"
								color="textSecondary"
								data-test-id="gas-allocations-total"
								sx={{ fontWeight: 600 }}
							>
								{`Total ${total.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`}
							</Typography>
						)}
						<Button
							variant="contained"
							color="primary"
							startIcon={<EditIcon />}
							disabled={isLoading || targets.length === 0}
							onClick={() => setShowModal("update")}
						>
							EDIT DEFAULT SPLIT
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<EditGasDefaultsModal
				targets={targets}
				modal={showModal}
				onHide={() => setShowModal("")}
			/>
		</>
	);
};
